import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { FeatureFlags, DEFAULT_FEATURE_FLAGS } from '../types/featureFlags';
import { getPublicConfig } from '../api/featureFlagsApi';
import { resolveFeatureFlags } from './resolveFeatureFlags';
import logger from '../utils/logger';

interface FeatureFlagsContextValue {
    flags: FeatureFlags;
    /** True until the first /config fetch settles (success or failure). */
    loading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
    flags: DEFAULT_FEATURE_FLAGS,
    loading: true,
});

/**
 * Loads feature flags from GET /config once at app start. Public, so it runs
 * regardless of auth. On any failure it keeps the fail-closed defaults, so an
 * unreleased feature never appears just because the fetch didn't land.
 */
export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        getPublicConfig()
            .then((config) => {
                if (cancelled) return;
                setFlags(resolveFeatureFlags(config.featureFlags));
            })
            .catch(() => {
                if (cancelled) return;
                logger.error('Failed to load feature flags; using fail-closed defaults');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const value = useMemo(() => ({ flags, loading }), [flags, loading]);
    return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

/** All resolved flags. */
export function useFeatureFlags(): FeatureFlags {
    return useContext(FeatureFlagsContext).flags;
}

/** A single flag by key, e.g. `useFeatureFlag('easyflexPage')`. */
export function useFeatureFlag(key: keyof FeatureFlags): boolean {
    return useContext(FeatureFlagsContext).flags[key];
}

/** True while the first /config fetch is in flight. */
export function useFeatureFlagsLoading(): boolean {
    return useContext(FeatureFlagsContext).loading;
}
